import { useState } from "react";
import type { TraceNode, TraceTarget } from "@/domain/trace";

const ENTITY_NOUN: Record<TraceTarget["kind"], { singular: string; plural: string }> = {
  spend: { singular: "spend ask", plural: "spend asks" },
  subProject: { singular: "sub-project", plural: "sub-projects" },
  project: { singular: "project", plural: "projects" },
  milestone: { singular: "milestone", plural: "milestones" },
  disbursement: { singular: "disbursement", plural: "disbursements" },
};

const SHOWN_BY_DEFAULT = 4;

/**
 * Lateral siblings under the current node's own parent (Trace Source spec
 * §3.4) — e.g. other spend asks in the same sub-project. Clicking a chip
 * replaces the current node in place rather than pushing a new ribbon
 * entry (spec §8.4): same depth, same header hop count.
 */
export function TraceSiblingShelf({
  siblings,
  parentName,
  entityKind,
  money,
  onWalk,
}: {
  siblings: TraceNode[];
  /** Name of the immediate parent the siblings share, for the label. */
  parentName?: string;
  entityKind: TraceTarget["kind"];
  money: (n: number) => string;
  onWalk: (target: TraceTarget) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const noun = ENTITY_NOUN[entityKind];

  if (siblings.length === 0) {
    return (
      <p className="mt-2 border-t border-dashed border-line pt-2.5 font-mono text-[10.5px] text-faint">
        Only {noun.singular} {parentName ? `in ${parentName}` : "here"}
      </p>
    );
  }

  const shown = expanded ? siblings : siblings.slice(0, SHOWN_BY_DEFAULT);
  const remaining = siblings.length - SHOWN_BY_DEFAULT;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-dashed border-line pt-2.5">
      <span className="font-mono text-[10.5px] tracking-wide text-faint">
        Other {noun.plural}{parentName ? ` in ${parentName}` : ""} ·
      </span>
      {shown.map((s, i) => (
        <button
          key={i}
          type="button"
          onClick={() => s.target && onWalk(s.target)}
          disabled={!s.target}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-2 py-1 text-xs text-mute transition-colors hover:border-brand hover:bg-brandSoft hover:text-brand disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-faint" aria-hidden />
          {s.label}
          {s.amount !== undefined && <span className="tabular-nums">· {money(s.amount)}</span>}
        </button>
      ))}
      {!expanded && remaining > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex items-center gap-1 rounded-md border border-line bg-card px-2 py-1 text-xs text-mute hover:bg-subtle"
        >
          ▾ {remaining} more
        </button>
      )}
    </div>
  );
}
